/*
 * 设置。
 *
 * 分两类，界面上也要分开，不能混成一张开关列表：
 *
 *   服务端设置  记忆开关、订阅消息。
 *               它们影响跨设备行为，必须存在服务端；连不上就不能改，
 *               改了却没存上比不让改更糟。
 *   本地偏好    纯视觉的东西，比如是否播放梦境动效。它不需要服务端，
 *               所以离线时**照常可用**——这也是这一页唯一能操作的部分。
 *
 * 不使用默认勾选诱导授权。但**「默认关」不是一条通用规则**，别照抄：
 * 真正涉及对外授权的（推送订阅）默认关；而「允许她记住我们的对话」
 * 服务端默认就是开的（`MEMORY_WRITE_DEFAULT`），关掉它等于把产品的核心行为关掉，
 * 默认关会让每个从没表过态的人都以为自己在用一个残缺版本。
 * 纯视觉的本地偏好同理默认开。开关的值一律以服务端下发的为准，前端不猜。
 *
 * 这一页曾经还有五块，都已按仓库主人要求撤掉，别照着旧版本加回来：
 *   - 「允许跨角色共享记忆」开关。`ConsentKind` 里没有这一项，送上去 400，
 *     而且只有一个梦灵时没有可共享的对象——一个永远关着、永远点不动的开关。
 *     第二个梦灵上线时连同后端一起加。
 *   - 「登录设备」一节。后端 `GET/DELETE app/auth/sessions` 仍在，见 `auth-api.ts`。
 *   - 「安静时间」。它约束的是**推送式问候**，而推送不存在；进入式开场按设计
 *     不受它约束（`docs/PROACTIVE.md` §1——用户凌晨三点自己打开 App，
 *     让她闭嘴是错的）。也就是说她只在你打开之后才开口，安静时间无论设成什么
 *     都不改变任何行为。服务端的 `quietHours` / `quietHoursEnforced` 与
 *     `PATCH` 入参都还在，推送（阶段 7）落地时前端再接。
 *   - 「允许她主动开口」开关。主动开口能力仍保留，但不再由设置页暴露给用户。
 *   - 「账号」一节与退出登录说明。退出账号入口保留在「我的资料」页。
 */

import { useEffect, useState } from "react";
import { AiDisclosureSection } from "../../components/AiDisclosure";
import { LoadingState, PageNotice } from "../../components/AsyncState";
import { Button, PageShell, Section } from "../../components/ui";
import { IconInfo, IconRetry, IconSettings } from "../../components/Icons";
import { ApiError } from "../../services/api-client";
import {
  fetchSettings,
  patchSettings,
  type SettingsPatch,
  type SettingsView,
} from "../../services/me-api";
import { useResource } from "../../state/resource";
import { useSession } from "../../state/session";
import "../pages.css";

const CALM_KEY = "miao.pref.calmStage";

/**
 * 舞台动效开关。本地偏好，不放任何账号信息。
 *
 * ⚠ **内部状态是 `calm`（安静 = 不放动效），界面上的开关是它的反面**
 * （「梦境特效」，开 = 有动效）。这么留着是因为 `calm` 才是系统里的那个概念：
 * CSS 类叫 `.calm-stage`，`prefers-reduced-motion: reduce` 做的也是同一件事。
 * 取反只在 `<Toggle>` 那一处发生，别把两种极性混着用。
 *
 * 存储键与取值也保持原样（`true` = 安静）：缺键 = 不安静 = 有动效，
 * 正好就是想要的默认值；改成存 `effects` 会让已经手动关过动效的人静默变回打开。
 */
function useCalmStage(): [boolean, (next: boolean) => void] {
  const [calm, setCalm] = useState(
    () => localStorage.getItem(CALM_KEY) === "true",
  );

  useEffect(() => {
    document.documentElement.classList.toggle("calm-stage", calm);
    localStorage.setItem(CALM_KEY, String(calm));
  }, [calm]);

  return [calm, setCalm];
}

function Toggle({
  checked,
  disabled,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange?: (next: boolean) => void;
  label: string;
  hint: string;
}) {
  return (
    <label className={`toggle${disabled ? " is-disabled" : ""}`}>
      <span className="toggle__text">
        <span className="toggle__label">{label}</span>
        <span className="toggle__hint">{hint}</span>
      </span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled ?? false}
        onChange={(event) => onChange?.(event.target.checked)}
      />
      <span className="toggle__track" aria-hidden>
        <span className="toggle__thumb" />
      </span>
    </label>
  );
}

export function SettingsPage() {
  const session = useSession();
  const [calm, setCalm] = useCalmStage();

  const data = useResource<SettingsView>((signal) => fetchSettings(signal), []);

  const settings = data.state.phase === "ready" ? data.state.data : null;

  /** 正在提交的键。留着键名而不是布尔量，是为了让出错时能说清是哪一项。 */
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * 改一个开关。
   *
   * ⚠ **不做乐观更新。** 这几项是授权状态，界面先翻过去再等服务端，
   * 失败时会出现「看起来关了其实还开着」的一瞬——涉及隐私的开关不能有这种歧义。
   * 宁可等一次往返，也不能让用户以为自己已经关掉了记忆。
   */
  const change = async (patch: SettingsPatch, key: string) => {
    setPending(key);
    setError(null);
    try {
      await patchSettings(patch);
      data.reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "没能存上，再试一次？");
    } finally {
      setPending(null);
    }
  };

  /**
   * 任一开关正在提交时锁住所有开关。
   *
   * 不做「只锁正在提交的那一个」：并发两个 PATCH 会让先返回的那个
   * `setPending(null)` 提前解锁，而后一个还在飞，紧接着两次 `reload()`
   * 竞争同一份数据。一次只改一项、等它落地，是这一页唯一说得清的行为。
   */
  const locked = !settings?.editable || pending !== null;

  return (
    <PageShell
      icon={<IconSettings size={20} />}
      title="设置"
      actions={
        data.state.phase === "failed" ? (
          <Button tone="quiet" icon={<IconRetry size={15} />} onClick={data.reload}>
            重试
          </Button>
        ) : undefined
      }
    >
      <Section title="这台设备">
        {/* 极性在 useCalmStage 的注释里，取反只发生在这里。 */}
        <Toggle
          checked={!calm}
          onChange={(next: boolean) => setCalm(!next)}
          label="梦境特效"
          hint="飘落的花瓣与灵体的光晕。关掉会安静一些，也更省电。系统开着「减弱动态效果」时它会自动停下，与这里的选择无关。"
        />
      </Section>

      <Section title="记忆与隐私">
        {data.state.phase === "loading" ? (
          <LoadingState label="正在读取设置" />
        ) : null}

        {data.state.phase === "failed" ? (
          <PageNotice
            scene="settings"
            error={data.state.error}
            onRetry={data.reload}
            expected={[
              "记忆总开关",
            ]}
          />
        ) : null}

        {/*
          ⚠ **拿到值之前一个开关都不画。**

          这里曾经在 loading 阶段就渲染，靠 `settings?.memoryEnabled ?? false`
          兜底——于是每次进设置页都先看见「关」，一个往返之后才翻成「开」。
          这项服务端默认就是开的，那一瞬的「关」是纯粹的谎，而且正好落在
          用户最在意的隐私开关上：看见「记忆：关闭」的人会以为她一直没在记。
          空着等一下比先说错话好。

          ⚠ 也不要因为「反正禁用着」就给 onChange 传一个空函数：
          受控 input 缺 onChange 会被 React 警告，而传空函数等于
          把「点了没反应」变成一个静默的谎。禁用态就真的不传 onChange。

          `settings.editable` 由服务端下发而不是前端写死。写能力已于
          2026-07-31 上线，它当前恒为 true——但保留这个分支：
          服务端要临时关掉写入（迁移、故障）时不必同时发一版前端。
        */}
        {settings ? (
          <>
            <Toggle
              checked={settings.memoryEnabled}
              disabled={locked}
              {...(settings.editable
                ? {
                    onChange: (next: boolean) =>
                      void change({ memoryEnabled: next }, "memoryEnabled"),
                  }
                : {})}
              label="允许她记住我们的对话"
              hint="关闭后新的对话不再提炼记忆，已有记忆保留，可在记忆中心单独删除。"
            />
            {error ? <p className="edit-error">{error}</p> : null}

            {!settings.editable ? (
              <p className="inline-hint">
                <IconInfo size={13} />
                这些开关暂时只能看不能改。显示的是它们此刻的真实状态，不是默认值。
              </p>
            ) : null}
          </>
        ) : null}
      </Section>

      <Section title="通知">
        <div className="capability-list">
          <CapabilityRow
            name="订阅消息"
            available={session.host.subscription.available}
            reason={session.host.subscription.reason}
          />
          <CapabilityRow
            name="录音"
            available={session.host.capabilities.audioRecording}
            reason={
              session.host.capabilities.audioRecording
                ? undefined
                : "当前浏览器不提供 MediaRecorder"
            }
          />
          <CapabilityRow
            name="微信支付"
            available={session.host.payment.available}
            reason={session.host.payment.reason}
          />
          <CapabilityRow
            name="激励视频广告"
            available={session.host.rewardedAd.available}
            reason={session.host.rewardedAd.reason}
          />
        </div>

        <p className="inline-hint">
          <IconInfo size={13} />
          当前宿主：{session.host.label}
          。能力由运行时探测得出并上报给服务端，服务端据此决定可用的功能。
        </p>
      </Section>

      <Section title="关于" hint="她是什么，以及不该拿她当什么">
        <AiDisclosureSection />
      </Section>
    </PageShell>
  );
}

/* -------------------------------------------------------------------------- */

function CapabilityRow({
  name,
  available,
  reason,
}: {
  name: string;
  available: boolean;
  reason?: string | undefined;
}) {
  return (
    <div className="capability-row">
      <span className="capability-row__name">{name}</span>
      <span
        className={`chip ${available ? "chip--brand" : "chip--muted"}`}
      >
        {available ? "可用" : "不可用"}
      </span>
      {reason ? <span className="capability-row__reason">{reason}</span> : null}
    </div>
  );
}
